import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth-store'
import { exportsService } from '@/features/file-exports/services/exports.service'
import { exportsKeys } from '@/features/file-exports/keys/exports.keys'
import type {
  ExportJobEvent,
  ExportJobListResponse,
} from '@/features/file-exports/types/export-job.types'

export function useExportEvents() {
  const queryClient = useQueryClient()
  const accessToken = useAuthStore((s) => s.accessToken)

  useEffect(() => {
    if (!accessToken) return

    const es = new EventSource(exportsService.getSseUrl(accessToken))

    es.addEventListener('export-job-updated', (e: MessageEvent) => {
      const event: ExportJobEvent = JSON.parse(e.data)

      const cachedPages = queryClient.getQueriesData<ExportJobListResponse>({
        queryKey: exportsKeys.all(),
      })
      const jobInCache = cachedPages.some(([, data]) =>
        data?.data.some((j) => j.id === event.jobId),
      )

      if (!jobInCache) {
        void queryClient.invalidateQueries({ queryKey: exportsKeys.all() })
        return
      }

      queryClient.setQueriesData<ExportJobListResponse>({ queryKey: exportsKeys.all() }, (old) => {
        if (!old) return old
        const idx = old.data.findIndex((j) => j.id === event.jobId)
        if (idx === -1) return old
        const updated = [...old.data]
        updated[idx] = {
          ...updated[idx],
          status: event.status,
          progress: event.progress,
          completedAt: event.completedAt,
          errorMessage: event.errorMessage,
        }
        return { ...old, data: updated }
      })
    })

    es.onerror = () => {
      queryClient.invalidateQueries({ queryKey: exportsKeys.all() })
    }

    return () => es.close()
  }, [accessToken, queryClient])
}
